import * as React from 'react';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker as MUIDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TextValidator } from 'react-material-ui-form-validator';

export type ISODateString = `${number}-${number}-${number}`;

export type AppDatePickerProps = {
    label?: string;
    name?: string;
    value: Date | string | null;
    onChange: (event: { target: { name: string; value: string | null } }) => void;
    disabled?: boolean;
    disabledDates?: Array<Date | string>;
    minDate?: Date;
    maxDate?: Date;
    minDateTime?: Date;
    maxDateTime?: Date;
    validators?: string[];
    errorMessages?: string[];
    slotProps?: { textField?: any };
    fullWidth?: boolean;
    disablePast?: boolean;
    disableFuture?: boolean;
    withTime?: boolean;
    emitUTC?: boolean;
    startTime?: string | Date;
    endTime?: string | Date;
    timeStepMinutes?: number;
    readOnly?: boolean
};

const pad = (n: number) => String(n).padStart(2, '0');
const toIsoDate = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toIsoDateTimeLocal = (d: Date) =>
    `${toIsoDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toIsoDateTimeUTC = (d: Date) =>
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(
        d.getUTCHours()
    )}:${pad(d.getUTCMinutes())}Z`;

const isIsoDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
const isIsoDateTime = (s: string) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s);
const isIsoDateTimeZ = (s: string) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?Z$/.test(s);

const parseMaybeDate = (v: Date | string | null): Date | null => {
    if (!v) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (typeof v === 'string') {
        if (isIsoDateTimeZ(v) || isIsoDateTime(v)) {
            const d = new Date(v);
            return isNaN(d.getTime()) ? null : d;
        }
        if (isIsoDate(v)) return new Date(`${v}T00:00:00`);
        const d = new Date(v);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
};

// Parse "HH:mm" into Date (time-of-day only)
const parseHHmm = (v?: string | Date) => {
    if (!v) return undefined;
    if (v instanceof Date) return v;
    const m = /^(\d{1,2}):(\d{2})$/.exec(v);
    if (!m) return undefined;
    const d = new Date(1970, 0, 1, Number(m[1]), Number(m[2]), 0, 0);
    return isNaN(d.getTime()) ? undefined : d;
};

// Build a Date on same calendar day as base, with time from t
const withTimeOfDay = (base: Date, t?: Date) => {
    if (!t) return base;
    const d = new Date(base);
    d.setHours(t.getHours(), t.getMinutes(), 0, 0);
    return d;
};

// Clamp dt between start/end (same-day window)
const clampToWindow = (dt: Date, start?: Date, end?: Date) => {
    if (!start && !end) return dt;
    const s = start ? withTimeOfDay(dt, start) : null;
    const e = end ? withTimeOfDay(dt, end) : null;
    if (s && dt < s) return s;
    if (e && dt > e) return e;
    return dt;
};

// Does this hour have at least one valid minute tick on the step grid?
const hourHasValidStep = (hour: number, min?: Date, max?: Date, step?: number) => {
    const ticks =
        step && step > 1
            ? Array.from({ length: Math.floor(60 / step) + 1 }, (_, i) => i * step).filter((m) => m <= 59)
            : Array.from({ length: 60 }, (_, i) => i);
    const minMinute = min && hour === min.getHours() ? min.getMinutes() : 0;
    const maxMinute = max && hour === max.getHours() ? max.getMinutes() : 59;
    return ticks.some((m) => m >= minMinute && m <= maxMinute);
};

export default function AppDatePicker({
    label = 'Select date',
    name = 'date',
    value,
    onChange,
    disabled,
    disabledDates = [],
    minDate,
    maxDate,
    minDateTime,
    maxDateTime,
    validators = [],
    errorMessages = [],
    slotProps,
    fullWidth = true,
    disablePast,
    disableFuture,
    withTime = false,
    emitUTC = false,
    startTime,
    endTime,
    timeStepMinutes,
    readOnly
}: AppDatePickerProps) {
    const parsedValue = React.useMemo(() => parseMaybeDate(value), [value]);

    const disabledIsoSet = React.useMemo(() => {
        const isoSet = new Set<string>();
        for (const item of disabledDates) {
            const d = parseMaybeDate(item as any);
            if (d) isoSet.add(toIsoDate(d));
        }
        return isoSet;
    }, [disabledDates]);

    const shouldDisableDate = React.useCallback(
        (date: Date) => disabledIsoSet.has(toIsoDate(date)),
        [disabledIsoSet]
    );

    const minTime = React.useMemo(() => parseHHmm(startTime), [startTime]);
    const maxTime = React.useMemo(() => parseHHmm(endTime), [endTime]);

    const shouldDisableTime = React.useCallback(
        (val: Date, view: 'hours' | 'minutes' | 'seconds') => {
            if (!withTime) return false;

            const h = val.getHours();
            const m = val.getMinutes();

            if (minTime && h < minTime.getHours()) return true;
            if (maxTime && h > maxTime.getHours()) return true;

            if (view === 'hours') {
                if (!hourHasValidStep(h, minTime, maxTime, timeStepMinutes)) return true;
                return false;
            }

            if (view === 'minutes') {
                if (minTime && h === minTime.getHours() && m < minTime.getMinutes()) return true;
                if (maxTime && h === maxTime.getHours() && m > maxTime.getMinutes()) return true;
            }

            return false;
        },
        [withTime, minTime, maxTime, timeStepMinutes]
    );

    const handleChange = React.useCallback(
        (newValue: Date | null) => {
            let v = newValue;
            if (v && withTime && (minTime || maxTime)) v = clampToWindow(v, minTime, maxTime);

            const out = v
                ? withTime
                    ? emitUTC
                        ? toIsoDateTimeUTC(v)
                        : toIsoDateTimeLocal(v)
                    : toIsoDate(v)
                : null;

            onChange({ target: { name, value: out } });
        },
        [emitUTC, name, onChange, withTime, minTime, maxTime]
    );

    const dateFormat = withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';

    const commonProps = {
        label,
        value: parsedValue,
        onChange: handleChange,
        shouldDisableDate,
        minDate,
        maxDate,
        disablePast,
        disableFuture,
        format: dateFormat,
        ampm: false,
        disabled,
        slots: { textField: TextValidator as any },
        slotProps: {
            textField: {
                name,
                validators,
                errorMessages,
                InputLabelProps: { shrink: true },
                fullWidth,
                placeholder: readOnly ? '' : (slotProps?.textField as any)?.placeholder,

                inputProps: {
                    ...((slotProps?.textField as any)?.inputProps ?? {}),
                    readOnly: readOnly,
                },

                onMouseDown: (e: React.MouseEvent) => {
                    if (readOnly) e.preventDefault();
                    (slotProps?.textField as any)?.onMouseDown?.(e);
                },

                onDoubleClick: (e: React.MouseEvent) => {
                    if (readOnly) e.preventDefault();
                    (slotProps?.textField as any)?.onDoubleClick?.(e);
                },
            } as any,
        },
    };

    return withTime ? (
        <MUIDateTimePicker
            {...commonProps}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            minTime={minTime}
            maxTime={maxTime}
            timeSteps={timeStepMinutes ? { minutes: timeStepMinutes } : undefined}
            shouldDisableTime={shouldDisableTime}
        />
    ) : (
        <MUIDatePicker {...commonProps} />
    );
}
